import { useActionConfirmation } from '../components/ActionFeedbackProvider';
// React and hooks
import React, { useRef, useState, useEffect } from 'react';
// Icons
import { Trash, Home, FileUp, FileDown, Sun, Moon, Pencil, Plus, Search, Tag } from 'lucide-react';
// Animation
import { motion } from 'framer-motion';
// Router
import { useNavigate } from 'react-router-dom';
// Hooks
import useTheme from '../hooks/useTheme';
// API
import api from '../lib/api';
// i18n
import { useTranslation } from 'react-i18next';
import PageHeading from '../components/PageHeading';

const BrandScreen = () => {
  const fileInputRef = useRef();
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [currentId, setCurrentId] = useState(null);
  const [brandForm, setBrandForm] = useState({ name: '' });

  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();

  const confirmAction = useActionConfirmation();

  // Base button styles (now using dark: classes instead of conditional theme logic)
  const baseBtn =
    "inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition duration-200 " +
    "bg-black text-white hover:bg-white hover:text-black hover:scale-105 disabled:opacity-60 " +
    "dark:bg-white dark:text-black dark:hover:bg-black dark:hover:text-white";

  // Fetch brands
  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      setError('');
      const { data } = await api.get('/brands');
      setBrands(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.message || t('errorLoadBrands'));
    } finally {
      setLoading(false);
    }
  };

  // Modal functions
  const openCreate = () => {
    setModalMode('create');
    setCurrentId(null);
    setBrandForm({ name: '' });
    setShowModal(true);
  };

  const openEdit = (brand) => {
    setModalMode('edit');
    setCurrentId(brand.id || brand._id);
    setBrandForm({ name: brand.name || '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentId(null);
    setBrandForm({ name: '' });
  };

  const handleSave = async () => {
    if (!brandForm.name.trim()) return;
    try {
      setBusyAction(true);
      if (modalMode === 'create') {
        await api.post('/brands', { name: brandForm.name.trim() });
      } else if (modalMode === 'edit' && currentId) {
        await api.put(`/brands/${currentId}`, { name: brandForm.name.trim() });
      }
      closeModal();
      await fetchBrands();
    } catch (err) {
      setError(err?.response?.data?.message || t('errorSaveBrand'));
    } finally {
      setBusyAction(false);
    }
  };

  const handleDeleteBrand = async (id) => {
    if (!await confirmAction(t('confirmDeleteBrand'))) return;
    try {
      setBusyAction(true);
      await api.delete(`/brands/${id}`);
      await fetchBrands();
    } catch (err) {
      setError(err?.response?.data?.message || t('errorDeleteBrand'));
    } finally {
      setBusyAction(false);
    }
  };

  // Import / Export
  const handleImportClick = () => fileInputRef.current?.click();
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setBusyAction(true);
      await api.post('/brands/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert(t('importBrandsSuccess'));
      await fetchBrands();
    } catch (err) {
      setError(err?.response?.data?.message || t('errorImportBrands'));
    } finally {
      setBusyAction(false);
      e.target.value = '';
    }
  };

  const handleExport = async () => {
    try {
      setBusyAction(true);
      const res = await api.get('/brands/export', { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'brands.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.message || t('errorExportBrands'));
    } finally {
      setBusyAction(false);
    }
  };

  const filteredBrands = brands
    .filter((brand) => String(brand.name || '').toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()))
    .sort((first, second) => String(first.name || '').localeCompare(String(second.name || ''), undefined, { sensitivity: 'base' }) * (sortOrder === 'asc' ? 1 : -1));

  return (
    <motion.div
      className="min-h-full px-6 py-8 relative"
      initial={false}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      {/* Theme toggle */}
      <button onClick={toggleTheme} className="absolute top-4 right-4 text-black dark:text-white">
        {theme === 'dark' ? <Sun size={22} /> : <Moon size={22} />}
      </button>

      {/* Main card */}
      <div className="relative z-10 max-w-4xl mx-auto bg-white dark:bg-gray-800 bg-opacity-90 shadow-xl rounded-xl p-6 backdrop-blur-md">
        <PageHeading icon={<Tag size={26} />} title={t('brands')} />
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex w-full gap-2 md:w-auto">
            <label className="flex min-w-0 flex-1 items-center rounded-lg bg-gray-100 px-3 py-2 dark:bg-gray-700 md:w-64">
              <Search size={16} className="mr-2 text-gray-500 dark:text-gray-300" />
              <input type="search" aria-label={t('search')} placeholder={t('search')} value={search} onChange={(event) => setSearch(event.target.value)} className="w-full bg-transparent text-sm text-gray-700 outline-none dark:text-gray-200" />
            </label>
            <select aria-label={t('sortAlphabetically')} value={sortOrder} onChange={(event) => setSortOrder(event.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200">
              <option value="asc">{t('sortAZ')}</option>
              <option value="desc">{t('sortZA')}</option>
            </select>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={openCreate} disabled={busyAction} className={baseBtn}>
              <Plus size={16} /> {t('add')}
            </button>
            <button onClick={handleImportClick} disabled={busyAction} className={baseBtn}>
              <FileUp size={16} /> {t('import')}
            </button>
            <button onClick={handleExport} disabled={busyAction} className={baseBtn}>
              <FileDown size={16} /> {t('export')}
            </button>
            <button onClick={() => navigate('/')} className={baseBtn}>
              <Home size={16} /> {t('home')}
            </button>
          </div>
        </div>

        <input type="file" accept=".xlsx" ref={fileInputRef} onChange={handleFileChange} className="hidden" />

        {error && <div className="mb-4 rounded-lg bg-red-600/90 text-white px-4 py-2">{error}</div>}

        {loading ? (
          <div className="text-gray-700 dark:text-gray-200">{t('loading')}</div>
        ) : filteredBrands.length > 0 ? (
          <div className="space-y-2">
            {filteredBrands.map((b) => {
              const id = b.id || b._id;
              return (
                <div
                  key={id}
                  className="flex items-center justify-between border border-gray-300 dark:border-gray-700 rounded-md p-3 bg-white dark:bg-gray-700 shadow-sm"
                >
                  <p className="text-sm font-medium text-gray-800 dark:text-white">{b.name}</p>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(b)} className="p-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Pencil size={16} />
                    </button>
                    <button onClick={() => handleDeleteBrand(id)} className="p-2 rounded-md bg-red-600 hover:bg-red-700 text-white">
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="border border-dashed border-gray-300 dark:border-gray-700 p-6 rounded-md text-gray-600 text-center dark:text-gray-300">
            {brands.length > 0 ? t('noResults') : t('noBrands')}
          </div>
        )}
      </div>

      {/* Modal create/edit */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50 p-4">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md w-full max-w-sm space-y-4">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white">
              {modalMode === 'create' ? t('addBrand') : t('editBrand')}
            </h2>

            <input
              type="text"
              placeholder={t('name')}
              value={brandForm.name}
              onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })}
              className="w-full border px-3 py-2 rounded-md dark:bg-gray-700 dark:text-white dark:border-gray-600"
              disabled={busyAction}
            />

            <div className="flex justify-end gap-2">
              <button onClick={closeModal} className={baseBtn} disabled={busyAction}>
                {t('cancel')}
              </button>
              <button onClick={handleSave} className={baseBtn} disabled={busyAction}>
                {busyAction ? t('saving') : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default BrandScreen;
